import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { resolveImageUrl } from '../components/ImageUploader';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { usePricing } from '../lib/pricing';
import {
  ShoppingCart, Trash2, Plus, Minus, Package, Truck, Wrench, CheckCircle2, ArrowLeft, User, LogIn, Store,
} from 'lucide-react';

const Cart = () => {
  const { items, updateQty, removeItem, clear, itemCount } = useCart();
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const priceOf = usePricing();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    company: customer?.company || '',
    start_date: '',
    end_date: '',
    warehouse_pickup: true,
    delivery_pickup: false,
    delivery_address: '',
    setup_teardown: false,
    setup_location: '',
    message: '',
  });

  const totalOriginal = items.reduce((sum, i) => sum + (i.price_per_day || 0) * i.quantity, 0);
  const totalDaily = items.reduce((sum, i) => {
    const p = priceOf(i);
    return sum + (p.hasDiscount ? p.discounted : p.original) * i.quantity;
  }, 0);
  const totalSavings = totalOriginal - totalDaily;

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = { ...form, items };
      const res = await api.post('/cart-quotes', payload);
      setSuccess(res.data);
      clear();
      toast({ title: 'Richiesta inviata!', description: 'Ti contatteremo a breve con disponibilità e prezzo.' });
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile inviare la richiesta.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center fade-in">
        <div className="w-20 h-20 mx-auto rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-6">
          <CheckCircle2 size={48} />
        </div>
        <h1 className="text-3xl font-black brand-text">Richiesta inviata con successo</h1>
        <p className="text-neutral-600 mt-2">
          Numero richiesta: <strong className="text-[#d62828]">{success.id.slice(0, 8).toUpperCase()}</strong>
        </p>
        <p className="text-neutral-700 mt-4">
          Riceverai una risposta via email a <strong>{success.email}</strong> con la disponibilità e il prezzo per il periodo richiesto.
        </p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/noleggio" className="px-5 py-2.5 bg-[#d62828] text-white rounded font-bold hover:bg-[#b41f1f]">Continua a navigare</Link>
          <Link to="/" className="px-5 py-2.5 border border-neutral-300 rounded font-bold hover:bg-neutral-50">Torna alla home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <div className="flex items-center gap-3 mb-2">
        <ShoppingCart className="text-[#d62828]" size={28} />
        <h1 className="text-3xl md:text-4xl font-black brand-text text-neutral-900">Carrello</h1>
      </div>
      <p className="text-neutral-600">{itemCount} articoli selezionati. Compila il form per ricevere disponibilità e preventivo.</p>

      {items.length === 0 ? (
        <div className="mt-12 text-center py-16 bg-neutral-50 rounded-xl border border-neutral-200">
          <Package size={48} className="text-neutral-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-neutral-700">Il carrello è vuoto</h2>
          <p className="text-neutral-500 mt-1">Sfoglia il catalogo e aggiungi i prodotti che ti interessano.</p>
          <Link to="/noleggio" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-[#d62828] text-white rounded font-bold hover:bg-[#b41f1f]">
            <ArrowLeft size={16} /> Vai al catalogo
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_400px] gap-8">
          {/* Items */}
          <div className="space-y-3">
            {items.map((it) => {
              const ip = priceOf(it);
              return (
              <div key={it.product_id} className="flex gap-4 p-4 bg-white border border-neutral-200 rounded-lg">
                <div className="w-24 h-24 flex-shrink-0 bg-neutral-50 rounded-lg overflow-hidden flex items-center justify-center p-1">
                  {it.image_url ? (
                    <img src={resolveImageUrl(it.image_url)} alt={it.product_name} className="max-w-full max-h-full w-auto h-auto object-contain" />
                  ) : (
                    <Package size={32} className="text-neutral-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {it.brand && <span className="text-xs uppercase tracking-wider text-neutral-500 font-bold">{it.brand}</span>}
                  <Link to={`/prodotto/${it.product_slug}`} className="block font-bold text-neutral-900 hover:text-[#d62828] line-clamp-2">{it.product_name}</Link>
                  {it.price_per_day > 0 && (
                    ip.hasDiscount ? (
                      <span className="text-sm block" data-testid="cart-item-price">
                        <span className="text-neutral-400 line-through mr-2">€ {ip.original.toFixed(2)}</span>
                        <span className="text-[#d62828] font-bold">€ {ip.discounted.toFixed(2)}</span>
                        <span className="text-neutral-500"> / giorno</span>
                        <span className="ml-2 inline-block bg-[#d62828] text-white text-[10px] font-black px-1.5 py-0.5 rounded">-{Math.round(ip.percent)}%</span>
                      </span>
                    ) : (
                      <span className="text-sm text-neutral-500" data-testid="cart-item-price">€ {ip.original.toFixed(2)} / giorno</span>
                    )
                  )}
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button onClick={() => removeItem(it.product_id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" aria-label="Rimuovi prodotto" title="Rimuovi dal carrello">
                    <Trash2 size={16} />
                  </button>
                  <div className="flex items-center border border-neutral-300 rounded">
                    <button
                      type="button"
                      onClick={() => updateQty(it.product_id, it.quantity - 1)}
                      disabled={it.quantity <= 1}
                      className="px-2 py-1 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Diminuisci quantità"
                      title={it.quantity <= 1 ? 'Minimo 1 — usa il cestino per rimuovere' : 'Diminuisci'}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-3 text-sm font-bold min-w-[2rem] text-center">{it.quantity}</span>
                    <button type="button" onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-1 hover:bg-neutral-50" aria-label="Aumenta quantità">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
            <div className="flex justify-between items-center pt-2">
              <button onClick={clear} className="text-sm text-neutral-500 hover:text-red-600 underline">Svuota carrello</button>
              <Link to="/noleggio" className="text-sm font-bold text-[#d62828] flex items-center gap-1">
                <ArrowLeft size={14} /> Continua a navigare
              </Link>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 space-y-4 h-fit lg:sticky lg:top-32">
            <h2 className="text-xl font-black brand-text">Richiedi Preventivo</h2>

            {!customer && (
              <div className="text-xs bg-white border border-neutral-200 rounded p-3 flex items-center gap-2">
                <User size={14} className="text-[#d62828]" />
                <span className="text-neutral-600">Sei un cliente fisso?</span>
                <Link to="/cliente/login" className="font-bold text-[#d62828] hover:underline">Accedi</Link>
                <span className="text-neutral-400">o</span>
                <Link to="/cliente/registrati" className="font-bold text-[#d62828] hover:underline">Registrati</Link>
              </div>
            )}
            {customer && (
              <div className="text-xs bg-green-50 border border-green-200 text-green-800 rounded p-3 flex items-center gap-2">
                <CheckCircle2 size={14} /> Connesso come <strong>{customer.email}</strong>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field label="Azienda" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              <Field label="Data inizio" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
              <Field label="Data fine" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
            </div>

            <div className="space-y-3 pt-2 border-t border-neutral-200">
              <label className="flex items-start gap-2 text-sm font-medium cursor-pointer" data-testid="cart-warehouse-pickup-label">
                <input
                  type="checkbox"
                  checked={form.warehouse_pickup}
                  onChange={(e) => setForm({ ...form, warehouse_pickup: e.target.checked, delivery_pickup: e.target.checked ? false : form.delivery_pickup })}
                  className="mt-1"
                  data-testid="cart-warehouse-pickup"
                />
                <span className="flex items-center gap-1"><Store size={14} className="text-[#d62828]" /> Ritiro c/o magazzino di Poggibonsi / Barberino Tavarnelle</span>
              </label>
              <label className="flex items-start gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.delivery_pickup}
                  onChange={(e) => setForm({ ...form, delivery_pickup: e.target.checked, warehouse_pickup: e.target.checked ? false : form.warehouse_pickup })}
                  className="mt-1"
                  data-testid="cart-delivery-pickup"
                />
                <span className="flex items-center gap-1"><Truck size={14} className="text-[#d62828]" /> Richiedo consegna e ritiro in location</span>
              </label>
              {form.delivery_pickup && (
                <Field label="Indirizzo location" value={form.delivery_address} onChange={(v) => setForm({ ...form, delivery_address: v })} placeholder="Indirizzo, città, CAP" />
              )}
              <label className="flex items-start gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={form.setup_teardown} onChange={(e) => setForm({ ...form, setup_teardown: e.target.checked })} className="mt-1" />
                <span className="flex items-center gap-1"><Wrench size={14} className="text-[#d62828]" /> Richiedo setup e smontaggio</span>
              </label>
              {form.setup_teardown && !form.delivery_pickup && (
                <Field label="Indirizzo setup" value={form.setup_location} onChange={(v) => setForm({ ...form, setup_location: v })} placeholder="Indirizzo dove eseguire il setup" />
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-neutral-700 mb-1">Note aggiuntive</label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
                placeholder="Esigenze tecniche, accessori richiesti..."
              />
            </div>

            {totalDaily > 0 && (
              <div className="bg-white p-3 rounded border border-neutral-200 space-y-1" data-testid="cart-summary-total">
                {totalSavings > 0 && (
                  <>
                    <div className="flex justify-between text-xs text-neutral-500">
                      <span>Totale listino/giorno</span>
                      <span className="line-through">€ {totalOriginal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-green-700 font-medium">
                      <span>Sconto cliente</span>
                      <span>− € {totalSavings.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-sm pt-1">
                  <span className="text-neutral-700 font-medium">Totale stimato/giorno</span>
                  <span className="font-bold text-[#d62828]" data-testid="cart-summary-total-value">€ {totalDaily.toFixed(2)}</span>
                </div>
              </div>
            )}
            <p className="text-xs text-neutral-500">Il prezzo definitivo verrà confermato in base al periodo, sconti applicabili e servizi richiesti.</p>

            <button
              type="submit"
              disabled={submitting || items.length === 0}
              className="w-full px-5 py-3 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] transition-colors disabled:opacity-60"
            >
              {submitting ? 'Invio in corso...' : 'Invia richiesta preventivo'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, placeholder }) => (
  <div>
    <label className="block text-xs font-bold text-neutral-700 mb-1">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
    />
  </div>
);

export default Cart;
